import { useEffect, useMemo, useState, Fragment } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Trophy,
  Star,
  Mail,
  Phone,
  ExternalLink,
  ArrowUpRight,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BackgroundCheckModal from "@/components/BackgroundCheckModal";
import BackgroundCheckBadge from "@/components/BackgroundCheckBadge";

const CAT_COLORS = {
  behavioral: "bg-blue-50 text-blue-700 border-blue-200",
  technical: "bg-violet-50 text-violet-700 border-violet-200",
  situational: "bg-amber-50 text-amber-800 border-amber-200",
};

function scoreClass(s) {
  if (s === null || s === undefined) return "text-slate-400";
  if (s >= 8) return "text-emerald-600";
  if (s >= 6) return "text-blue-600";
  if (s >= 4) return "text-amber-600";
  return "text-rose-600";
}

function scorePill(s) {
  if (s === null || s === undefined)
    return (
      <span className="inline-flex items-center border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        N/A
      </span>
    );
  const cls =
    s >= 8
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : s >= 6
        ? "border-blue-200 bg-blue-50 text-blue-700"
        : s >= 4
          ? "border-amber-200 bg-amber-50 text-amber-800"
          : "border-rose-200 bg-rose-50 text-rose-700";
  return (
    <span
      className={`inline-flex items-center border px-2 py-0.5 text-[11px] font-bold tabular-nums ${cls}`}
    >
      {Number(s).toFixed(1)}
    </span>
  );
}

export default function Compare() {
  const [params] = useSearchParams();
  const idsParam = params.get("ids") || "";
  const ids = useMemo(
    () => idsParam.split(",").filter(Boolean).slice(0, 4),
    [idsParam]
  );

  const [loading, setLoading] = useState(() => ids.length >= 2);
  const [candidates, setCandidates] = useState([]);
  const [bgChecksByResume, setBgChecksByResume] = useState({});
  const [bgModalResumeId, setBgModalResumeId] = useState(null);

  useEffect(() => {
    if (ids.length < 2) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/applicants/compare", {
          resume_ids: ids,
        });
        if (cancelled) return;
        setCandidates(data?.candidates || []);
        // Fetch background checks per candidate in parallel
        const cands = data?.candidates || [];
        const results = await Promise.all(
          cands.map((c) =>
            api
              .get("/background-checks", { params: { resume_id: c.resume_id } })
              .then((r) => [c.resume_id, r.data?.checks || []])
              .catch(() => [c.resume_id, []])
          )
        );
        if (cancelled) return;
        const map = {};
        results.forEach(([rid, checks]) => {
          map[rid] = checks;
        });
        setBgChecksByResume(map);
      } catch (e) {
        if (cancelled) return;
        toast.error(
          e?.response?.data?.detail || "Could not load comparison"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ids]);

  // Find winner per question (highest score across candidates)
  const winnersByQ = useMemo(() => {
    const map = {};
    const allQs = new Set();
    candidates.forEach((c) =>
      (c.answers || []).forEach((a) => allQs.add(a.question_id))
    );
    allQs.forEach((qid) => {
      let best = -1;
      let winnerIdx = -1;
      candidates.forEach((c, idx) => {
        const a = (c.answers || []).find((x) => x.question_id === qid);
        const s = a?.score ?? -1;
        if (s > best) {
          best = s;
          winnerIdx = idx;
        }
      });
      if (best > 0) map[qid] = winnerIdx;
    });
    return map;
  }, [candidates]);

  // Top weighted score
  const topWeightedIdx = useMemo(() => {
    let best = -1;
    let idx = -1;
    candidates.forEach((c, i) => {
      const s = c.weighted_score ?? -1;
      if (s > best) {
        best = s;
        idx = i;
      }
    });
    return best > 0 ? idx : -1;
  }, [candidates]);

  // Unified question rows (union across candidates, preserve job's question order
  // by taking from the first candidate that has questions)
  const questionRows = useMemo(() => {
    const seen = new Set();
    const rows = [];
    candidates.forEach((c) =>
      (c.answers || []).forEach((a) => {
        if (!seen.has(a.question_id)) {
          seen.add(a.question_id);
          rows.push({
            question_id: a.question_id,
            question: a.question,
            category: a.category,
          });
        }
      })
    );
    return rows;
  }, [candidates]);

  if (ids.length < 2) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <div className="border border-amber-200 bg-amber-50 p-6 text-amber-900">
          <h1 className="text-xl font-bold">
            Pick at least 2 candidates to compare
          </h1>
          <p className="mt-2 text-sm">
            Head over to the Applicants page, select 2–4 candidates, and click
            &ldquo;Compare&rdquo;.
          </p>
          <Link
            to="/applicants"
            data-testid="compare-back-to-applicants"
            className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-blue-700 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Applicants
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10"
      data-testid="compare-page"
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/applicants"
            data-testid="compare-back-link"
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft className="h-3 w-3" /> Applicants
          </Link>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Head-to-head comparison
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {candidates.length} candidate{candidates.length === 1 ? "" : "s"} ·
            scored using each role&apos;s rubric
          </p>
        </div>
      </div>

      {loading ? (
        <div
          data-testid="compare-loading"
          className="flex h-40 items-center justify-center text-slate-400"
        >
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : candidates.length === 0 ? (
        <div className="border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
          No candidates found for those ids.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div
            data-testid="compare-grid"
            className="grid gap-px bg-slate-200"
            style={{
              gridTemplateColumns: `200px repeat(${candidates.length}, minmax(240px, 1fr))`,
              minWidth: `${200 + candidates.length * 260}px`,
            }}
          >
            {/* Header row */}
            <div className="bg-slate-50 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
              Candidate
            </div>
            {candidates.map((c, i) => (
              <div
                key={c.resume_id}
                data-testid={`compare-card-header-${i}`}
                className={`bg-white px-4 py-4 ${
                  topWeightedIdx === i
                    ? "border-t-4 border-emerald-500"
                    : "border-t-4 border-transparent"
                }`}
              >
                {topWeightedIdx === i && (
                  <div className="mb-1 inline-flex items-center gap-1 border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                    <Trophy className="h-3 w-3" /> Top weighted
                  </div>
                )}
                <div
                  data-testid={`compare-name-${i}`}
                  className="text-base font-bold text-slate-900"
                >
                  {c.candidate_name || "Unnamed"}
                </div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  {c.application_ref || "—"}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {c.job_title || "—"}
                </div>
                <div className="mt-2 space-y-1 text-[11px] text-slate-600">
                  {c.candidate_email && (
                    <div className="flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {c.candidate_email}
                    </div>
                  )}
                  {c.candidate_phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {c.candidate_phone}
                    </div>
                  )}
                </div>
                {c.interview_id && c.job_id && (
                  <Link
                    to={`/jobs/${c.job_id}/candidates/${c.interview_id}`}
                    data-testid={`compare-open-review-${i}`}
                    className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 hover:underline"
                  >
                    Open full review <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
                <button
                  type="button"
                  data-testid={`compare-order-bg-${i}`}
                  onClick={() => setBgModalResumeId(c.resume_id)}
                  className="mt-2 inline-flex w-full items-center justify-center gap-1 border border-emerald-300 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:bg-emerald-50"
                >
                  <ShieldCheck className="h-3 w-3" />
                  Order background check
                </button>
                {(bgChecksByResume[c.resume_id] || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(bgChecksByResume[c.resume_id] || [])
                      .slice(0, 2)
                      .map((bg) => (
                        <BackgroundCheckBadge
                          key={bg.id}
                          status={bg.status}
                          assessment={bg.assessment}
                        />
                      ))}
                  </div>
                )}
              </div>
            ))}

            {/* Résumé score */}
            <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700">
              Résumé score
            </div>
            {candidates.map((c, i) => (
              <div
                key={`rs-${c.resume_id}`}
                className="bg-white px-4 py-3"
                data-testid={`compare-resume-score-${i}`}
              >
                <span
                  className={`text-2xl font-extrabold tabular-nums ${scoreClass(
                    c.resume_score / 10
                  )}`}
                >
                  {c.resume_score ?? "—"}
                </span>
                <span className="ml-1 text-xs text-slate-400">/100</span>
              </div>
            ))}

            {/* Overall interview score */}
            <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700">
              Overall interview score
            </div>
            {candidates.map((c, i) => (
              <div
                key={`os-${c.resume_id}`}
                className="bg-white px-4 py-3"
                data-testid={`compare-overall-score-${i}`}
              >
                <span
                  className={`text-2xl font-extrabold tabular-nums ${scoreClass(
                    c.overall_score
                  )}`}
                >
                  {c.overall_score !== null && c.overall_score !== undefined
                    ? Number(c.overall_score).toFixed(1)
                    : "—"}
                </span>
                <span className="ml-1 text-xs text-slate-400">/10</span>
              </div>
            ))}

            {/* Weighted score (per-job rubric) */}
            <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700">
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 text-emerald-600" /> Weighted score
              </span>
              <span className="mt-0.5 block text-[10px] font-normal text-slate-400">
                Rubric-weighted
              </span>
            </div>
            {candidates.map((c, i) => (
              <div
                key={`ws-${c.resume_id}`}
                className={`px-4 py-3 ${
                  topWeightedIdx === i ? "bg-emerald-50" : "bg-white"
                }`}
                data-testid={`compare-weighted-score-${i}`}
              >
                <span
                  className={`text-2xl font-extrabold tabular-nums ${scoreClass(
                    c.weighted_score
                  )}`}
                >
                  {c.weighted_score !== null && c.weighted_score !== undefined
                    ? Number(c.weighted_score).toFixed(2)
                    : "—"}
                </span>
                <span className="ml-1 text-xs text-slate-400">/10</span>
                {c.category_weights && (
                  <div className="mt-1 text-[10px] text-slate-400">
                    B {c.category_weights.behavioral ?? 1} · T{" "}
                    {c.category_weights.technical ?? 1} · S{" "}
                    {c.category_weights.situational ?? 1}
                  </div>
                )}
              </div>
            ))}

            {/* Status */}
            <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700">
              Status
            </div>
            {candidates.map((c, i) => (
              <div
                key={`st-${c.resume_id}`}
                className="bg-white px-4 py-3"
                data-testid={`compare-status-${i}`}
              >
                <span className="inline-flex items-center border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold capitalize text-slate-700">
                  {(c.application_status || "new").replace(/_/g, " ")}
                </span>
              </div>
            ))}

            {/* Summary */}
            <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700">
              Résumé summary
            </div>
            {candidates.map((c, i) => (
              <div
                key={`sm-${c.resume_id}`}
                className="bg-white px-4 py-3 text-xs text-slate-700"
                data-testid={`compare-summary-${i}`}
              >
                {c.resume_summary || (
                  <span className="text-slate-400">No summary</span>
                )}
              </div>
            ))}

            {/* Skills */}
            <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700">
              Top skills
            </div>
            {candidates.map((c, i) => (
              <div
                key={`sk-${c.resume_id}`}
                className="bg-white px-4 py-3"
                data-testid={`compare-skills-${i}`}
              >
                <div className="flex flex-wrap gap-1">
                  {(c.skills || []).length === 0 ? (
                    <span className="text-xs text-slate-400">—</span>
                  ) : (
                    c.skills.map((sk, j) => (
                      <span
                        key={j}
                        className="inline-flex items-center border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700"
                      >
                        {sk}
                      </span>
                    ))
                  )}
                </div>
              </div>
            ))}

            {/* Highlights */}
            <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700">
              Highlights
            </div>
            {candidates.map((c, i) => (
              <div
                key={`hl-${c.resume_id}`}
                className="bg-white px-4 py-3"
                data-testid={`compare-highlights-${i}`}
              >
                {(c.highlights || []).length === 0 ? (
                  <span className="text-xs text-slate-400">—</span>
                ) : (
                  <ul className="space-y-1 text-xs text-slate-700">
                    {c.highlights.map((h, j) => (
                      <li key={j} className="flex gap-1">
                        <span className="text-emerald-600">+</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* Concerns */}
            <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700">
              Concerns
            </div>
            {candidates.map((c, i) => (
              <div
                key={`cn-${c.resume_id}`}
                className="bg-white px-4 py-3"
                data-testid={`compare-concerns-${i}`}
              >
                {(c.concerns || []).length === 0 ? (
                  <span className="text-xs text-slate-400">—</span>
                ) : (
                  <ul className="space-y-1 text-xs text-slate-700">
                    {c.concerns.map((h, j) => (
                      <li key={j} className="flex gap-1">
                        <span className="text-amber-600">!</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* Per-question scores */}
            {questionRows.map((qr, qIdx) => (
              <Fragment key={`qr-${qr.question_id}`}>
                <div className="bg-slate-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Q{qIdx + 1}
                    </span>
                    {qr.category && (
                      <span
                        className={`inline-flex items-center border px-1.5 py-0.5 text-[10px] font-semibold capitalize ${
                          CAT_COLORS[qr.category] || CAT_COLORS.behavioral
                        }`}
                      >
                        {qr.category}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-700">
                    {qr.question || "—"}
                  </div>
                </div>
                {candidates.map((c, i) => {
                  const a = (c.answers || []).find(
                    (x) => x.question_id === qr.question_id
                  );
                  const isWinner = winnersByQ[qr.question_id] === i;
                  return (
                    <div
                      key={`qa-${qr.question_id}-${c.resume_id}`}
                      className={`px-4 py-3 ${
                        isWinner ? "bg-emerald-50" : "bg-white"
                      }`}
                      data-testid={`compare-q-${qIdx}-c-${i}`}
                    >
                      <div className="mb-2 flex items-center gap-2">
                        {scorePill(a?.score)}
                        {isWinner && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                            <Trophy className="h-3 w-3" /> Best
                          </span>
                        )}
                      </div>
                      {a?.feedback && (
                        <div className="text-[11px] italic text-slate-500">
                          &ldquo;{a.feedback.slice(0, 140)}
                          {a.feedback.length > 140 ? "…" : ""}&rdquo;
                        </div>
                      )}
                      {a?.transcript && (
                        <details className="mt-2">
                          <summary
                            data-testid={`compare-transcript-toggle-${qIdx}-${i}`}
                            className="cursor-pointer text-[10px] font-bold uppercase tracking-wider text-blue-700"
                          >
                            Transcript
                          </summary>
                          <div className="mt-1 max-h-32 overflow-y-auto text-[11px] text-slate-700">
                            {a.transcript}
                          </div>
                        </details>
                      )}
                      {!a && (
                        <span className="text-[11px] text-slate-400">
                          No answer
                        </span>
                      )}
                    </div>
                  );
                })}
              </Fragment>
            ))}

            {/* Gap analysis */}
            <div className="bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-700">
              AI gap analysis
            </div>
            {candidates.map((c, i) => (
              <div
                key={`ga-${c.resume_id}`}
                className="bg-white px-4 py-3"
                data-testid={`compare-gap-${i}`}
              >
                {c.gap_analysis?.gaps?.length ? (
                  <ul className="space-y-1 text-[11px] text-slate-700">
                    {c.gap_analysis.gaps.slice(0, 3).map((g, j) => (
                      <li key={j} className="flex gap-1">
                        <span className="text-amber-600">!</span>
                        <span>
                          <span className="font-semibold">{g.area}:</span>{" "}
                          {g.detail}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex justify-end">
        <Link to="/applicants">
          <Button
            variant="outline"
            data-testid="compare-done-btn"
            className="border-slate-300"
          >
            <ArrowUpRight className="mr-2 h-4 w-4" />
            Back to Applicants
          </Button>
        </Link>
      </div>
      <BackgroundCheckModal
        open={!!bgModalResumeId}
        onOpenChange={(o) => !o && setBgModalResumeId(null)}
        resumeId={bgModalResumeId}
        candidateName={
          candidates.find((x) => x.resume_id === bgModalResumeId)
            ?.candidate_name || ""
        }
        candidateEmail={
          candidates.find((x) => x.resume_id === bgModalResumeId)
            ?.candidate_email || ""
        }
        onOrdered={(bg) =>
          setBgChecksByResume((prev) => ({
            ...prev,
            [bg.resume_id]: [bg, ...(prev[bg.resume_id] || [])],
          }))
        }
      />
    </div>
  );
}
